import Routes from 'routes/Routes';

import { useEffect, useMemo, useRef, useState } from 'react';

import 'react-quill/dist/quill.snow.css';
import { Route, BrowserRouter as Router, Switch } from 'react-router-dom';

import SharedLesson from 'pages/SharedLesson/SharedLesson';

import 'assets/styles/App.scss';
import { changeLanguage } from 'assets/translations/languageHandler';
import ConfirmationModalContextProvider from 'components/common/ConfirmationModal/confirmationModalContextProvider';
import LoaderWithFade from 'components/common/LoaderWithFade/LoaderWithFade';
import ScrollToTop from 'components/common/ScrollToTop/ScrollToTop';
import CookieBanner from 'components/logic/CookieBanner/CookieBanner';
import { initializeGoogleAnalyticsIfCookieBannerAccepted } from 'components/logic/CookieBanner/GoogleAnalyticsHandler';
import useAuthentication from 'hooks/api/useAuthentication';
import useCurrentLanguage from 'hooks/generic/useCurrentLanguage';
import useFeatureFlagChecker from 'hooks/generic/useFeatureFlagChecker';
import useLanguages from 'hooks/generic/useLanguages';
import actions from 'redux/app.slice';
import actionsAuth from 'redux/authentication/auth.slice';
import { useAppDispatch } from 'redux/hooks';
import createScript from 'utils/createScript';

function App() {
  const STRIPE_ENABLED = useFeatureFlagChecker('REACT_APP_STRIPE_ENABLED');
  const dpiMesure = useRef(null);
  const languages = useLanguages();

  const currentLanguage = useCurrentLanguage();
  const preSelectedLanguage = useMemo(() => {
    const langRegex = /lang=([a-z]{2})/i;
    const match = langRegex.exec(location.href);

    if (!match || !match[1]) {
      return currentLanguage;
    }

    const preSelectedLang = match[1];

    return languages.some(obj => obj.id === preSelectedLang) ? preSelectedLang : currentLanguage;
  }, []);

  useEffect(() => {
    // If opened from the website
    if ((location.href as string).includes('#register')) {
      dispatch(actionsAuth.setOpenedModal('register'));
    }
    // If opened from the website
    if ((location.href as string).includes('#login')) {
      dispatch(actionsAuth.setOpenedModal('login'));
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === '*' && (e.ctrlKey || e.metaKey)) {
        dispatch(actions.toogleDevMode());
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const dispatch = useAppDispatch();

  const [showLoader, setShowLoader] = useState(true);
  const [requestPath] = useState(location.pathname);
  const { getCurrentUser } = useAuthentication();

  useEffect(() => {
    initializeGoogleAnalyticsIfCookieBannerAccepted();
    (async () => {
      await getCurrentUser();
      await changeLanguage(preSelectedLanguage);
      setShowLoader(false);
    })();
  }, []);
  useEffect(() => {
    if (!dpiMesure.current) return;
    window.dpi = dpiMesure.current;
    //     = dpiMesure.current.offsetHeight;
  }, [dpiMesure]);

  useEffect(() => {
    if (STRIPE_ENABLED) {
      createScript('stripe-pricing', null, 'https://js.stripe.com/v3/pricing-table.js', true);
    }
  }, [STRIPE_ENABLED]);

  if (showLoader) {
    return <LoaderWithFade className="absoluteCenter" />;
  }

  return (
    <Router>
      <div ref={dpiMesure} id="dpi"></div>
      <ScrollToTop>
        <ConfirmationModalContextProvider>
          <Switch>
            {/* Public, no-login lesson download opened from a shared link. */}
            <Route path="/shared-lesson/:id" component={SharedLesson} />
            <Route path="/" render={() => <Routes requestPath={requestPath} />} />
          </Switch>
        </ConfirmationModalContextProvider>
      </ScrollToTop>
      <CookieBanner />
    </Router>
  );
}

export default App;
